/**
 * @fileoverview Componente de gerenciamento de produtos (admin).
 * Exibe todos os produtos (ativos, inativos e removidos) em tabela paginada.
 * Permite visualizar detalhes e editar o status (ativo/inativo) de cada produto.
 *
 * @module ProductManager
 * @since 1.0.0
 */

import { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  IconButton,
  Box,
  useMediaQuery,
  useTheme,
  Tooltip,
  Chip,
  TextField,
  Alert,
  Snackbar,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ProductEditModal from './ProductEditModal';
import { apiRequestJson } from '../../api/apiService';
import { setProducts } from '../../store/slices/globalDataSlice';
import globalContextManager from '../../utils/globalContextManager';

/**
 * Determina label e cor do chip de status do produto.
 * @param {Object} product
 * @returns {{ label: string, color: string }}
 */
const getStatusChip = (product) => {
  if (product.removido === true || product.removido === 1) return { label: 'Removido', color: 'error' };
  if (product.ativo === false || product.ativo === 0) return { label: 'Inativo', color: 'warning' };
  return { label: 'Ativo', color: 'success' };
};

/**
 * Componente de gerenciamento de produtos para administradores.
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.open - Estado de abertura do modal
 * @param {Function} props.onClose - Callback para fechar modal
 * @returns {JSX.Element}
 */
export default function ProductManager({ open, onClose }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();

  const [products, setProductsLocal] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [modalMode, setModalMode] = useState('view');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [orderBy, setOrderBy] = useState('nome');
  const [order, setOrder] = useState('asc');

  /**
   * Busca todos os produtos da API (ativos, inativos e removidos).
   * @async
   */
  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await apiRequestJson('products_list_all');
      if (response.success && response.data) {
        setProductsLocal(response.data);
      }
    } catch (error) {
      console.error('Erro ao buscar produtos:', error);
      showSnackbar('Erro ao carregar produtos', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchProducts();
      setPage(0);
    }
  }, [open]);

  /**
   * Exibe mensagem de feedback.
   * @param {string} message
   * @param {'success'|'error'} severity
   */
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  /**
   * Manipula ordenação da tabela.
   * @param {string} property
   */
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  /**
   * Produtos filtrados por busca e ordenados.
   */
  const filteredAndSorted = useMemo(() => {
    // filter: aplica busca por nome, categoria ou marca
    let filtered = searchTerm
      ? products.filter(
          (p) =>
            p.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            p.categoria?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            p.marca?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : products;

    // Ordenação
    return [...filtered].sort((a, b) => {
      let aVal = a[orderBy] ?? '';
      let bVal = b[orderBy] ?? '';
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      if (order === 'asc') return aVal > bVal ? 1 : -1;
      return aVal < bVal ? 1 : -1;
    });
  }, [products, searchTerm, order, orderBy]);

  /**
   * Abre modal no modo visualização.
   * @param {Object} product
   */
  const handleView = (product) => {
    setSelectedProduct(product);
    setModalMode('view');
    setEditModalOpen(true);
  };

  /**
   * Abre modal no modo edição.
   * @param {Object} product
   */
  const handleEdit = (product) => {
    setSelectedProduct(product);
    setModalMode('edit');
    setEditModalOpen(true);
  };

  /**
   * Salva alteração de status do produto via API e atualiza estado global.
   * @async
   * @param {{ id: string, active: boolean }} data
   */
  const handleSaveProduct = async ({ id, active }) => {
    const response = await apiRequestJson('products_update', {
      params: { id },
      body: { active },
    });

    if (!response.success) {
      throw new Error(response.message || 'Erro ao atualizar produto');
    }

    // Atualiza lista local
    setProductsLocal((prev) =>
      // map: atualiza o produto alterado na lista local
      prev.map((p) => (p.id === id ? { ...p, ativo: active ? 1 : 0 } : p))
    );

    // Atualiza contexto global
    await globalContextManager.forceUpdate('products');
    const updatedResponse = await apiRequestJson('products_list_all');
    if (updatedResponse.success && updatedResponse.data) {
      dispatch(setProducts(updatedResponse.data));
    }

    showSnackbar('Produto atualizado com sucesso');
  };

  const handleSearch = () => setSearchTerm(searchInput);
  const handleClearSearch = () => { setSearchInput(''); setSearchTerm(''); };

  /**
   * Renderiza tabela desktop.
   */
  const renderDesktopTable = () => (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>
              <TableSortLabel active={orderBy === 'id'} direction={orderBy === 'id' ? order : 'asc'} onClick={() => handleSort('id')}>
                ID
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel active={orderBy === 'nome'} direction={orderBy === 'nome' ? order : 'asc'} onClick={() => handleSort('nome')}>
                Nome
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel active={orderBy === 'categoria'} direction={orderBy === 'categoria' ? order : 'asc'} onClick={() => handleSort('categoria')}>
                Categoria
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel active={orderBy === 'ativo'} direction={orderBy === 'ativo' ? order : 'asc'} onClick={() => handleSort('ativo')}>
                Status
              </TableSortLabel>
            </TableCell>
            <TableCell align="right">Ações</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredAndSorted
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((product) => {
              const status = getStatusChip(product);
              return (
                <TableRow key={product.id}>
                  <TableCell sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {product.id}
                  </TableCell>
                  <TableCell>{product.nome}</TableCell>
                  <TableCell>{product.categoria || '-'}</TableCell>
                  <TableCell>
                    <Chip label={status.label} color={status.color} size="small" />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Visualizar">
                      <IconButton size="small" onClick={() => handleView(product)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => handleEdit(product)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  /**
   * Renderiza tabela mobile.
   */
  const renderMobileTable = () => (
    <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAndSorted
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((product) => {
                const status = getStatusChip(product);
                return (
                  <TableRow key={product.id}>
                    <TableCell>{product.nome}</TableCell>
                    <TableCell>
                      <Chip label={status.label} color={status.color} size="small" />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleView(product)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleEdit(product)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          Gerenciamento de Produtos
          <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Buscar por nome, categoria ou marca..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: (
                  <IconButton size="small" onClick={handleSearch}>
                    <SearchIcon />
                  </IconButton>
                ),
                endAdornment: searchInput && (
                  <IconButton size="small" onClick={handleClearSearch}>
                    <CloseIcon />
                  </IconButton>
                ),
              }}
              sx={{ flexGrow: 1, minWidth: 200 }}
            />
          </Box>

          {loading ? (
            <Alert severity="info">Carregando produtos...</Alert>
          ) : (
            isMobile ? renderMobileTable() : renderDesktopTable()
          )}

          <TablePagination
            component="div"
            count={filteredAndSorted.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage="Linhas por página:"
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <ProductEditModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        product={selectedProduct}
        mode={modalMode}
        onSave={handleSaveProduct}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
